import { computed, nextTick, readonly, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useAuthStore } from '@/stores/useAuthStore'
import type { LogoutChatOutcome } from '@/stores/useAuthStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'

const isLogoutConfirmVisible = ref(false)
const isLogoutLoading = ref(false)
const logoutChatOutcome = ref<LogoutChatOutcome | 'loading'>('none')
const activeWebphoneStatuses = new Set([
  'incoming',
  'dialing',
  'ringing',
  'calling',
  'connected',
  'held',
])

const waitForPaint = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })

export function useLogoutConfirm() {
  const authStore = useAuthStore()
  const workspace = useWorkspaceStore()
  const { t } = useI18n()
  const hasActiveWebphoneCall = computed(() =>
    activeWebphoneStatuses.has(workspace.webphoneCallStatus),
  )

  const closeLogoutConfirm = () => {
    if (isLogoutLoading.value) return

    isLogoutConfirmVisible.value = false
  }

  const confirmLogout = () => {
    if (isLogoutConfirmVisible.value || isLogoutLoading.value) return

    isLogoutConfirmVisible.value = true
    logoutChatOutcome.value = 'loading'
    authStore.getLogoutChatOutcome().then((outcome) => {
      logoutChatOutcome.value = outcome
    })
  }

  const logoutChatOutcomeMessage = computed(() => {
    switch (logoutChatOutcome.value) {
      case 'keep_open':
        return t('auth.session.logout_chats_keep_open')
      case 'release':
        return t('auth.session.logout_chats_released')
      case 'mixed':
        return t('auth.session.logout_chats_mixed')
      default:
        return null
    }
  })

  const releaseWebphoneForLogout = async () => {
    await new Promise<void>((resolve) => {
      const detail = { handled: false, complete: resolve }
      window.dispatchEvent(new CustomEvent('calloperator:webphone-logout', { detail }))

      if (!detail.handled) {
        const streams = [
          window.__callOperatorWebphoneMedia?.localStream,
          window.__callOperatorWebphoneMedia?.remoteStream,
        ]
        streams.forEach((stream) => stream?.getAudioTracks().forEach((track) => track.stop()))
        window.__callOperatorWebphoneMedia = undefined
        resolve()
      }
    })
  }

  const acceptLogout = async () => {
    if (isLogoutLoading.value) return

    isLogoutLoading.value = true
    try {
      await nextTick()
      await waitForPaint()
      await releaseWebphoneForLogout()
      await authStore.logout()
      isLogoutConfirmVisible.value = false
    } finally {
      isLogoutLoading.value = false
    }
  }

  return {
    acceptLogout,
    closeLogoutConfirm,
    confirmLogout,
    isLogoutConfirmVisible: readonly(isLogoutConfirmVisible),
    isLogoutLoading: readonly(isLogoutLoading),
    logoutAcceptLabel: t('auth.session.logout'),
    logoutCancelLabel: t('common.general.cancel'),
    logoutConfirmMessage: computed(() =>
      hasActiveWebphoneCall.value
        ? t('auth.session.logout_active_call_message')
        : t('auth.session.logout_confirm_message'),
    ),
    logoutConfirmTitle: t('auth.session.logout_confirm_title'),
    logoutChatOutcome: readonly(logoutChatOutcome),
    logoutChatOutcomeMessage,
    logoutChatsCheckingLabel: t('auth.session.logout_chats_checking'),
  }
}